import {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import classNames from 'classnames'
import Customer from '../../utils/customer'
import {colors} from '../../utils/colors'
import {
    DateFormats,
    formatTimeString,
    getValueFromTime,
    isTimeAfterOrEqual,
    isTimeBeforeOrEqual,
    isBeforeOrEqual,
    isAfterOrEqual,
    compareTime,
    isTimeBefore,
    isTimeAfter,
    utcToLocal,
    normalize,
    formatDate
} from '../../utils/date'
import renderSkeletonLoaders from '../../utils/loader'
import TeamSwitcher from '../../components/pages/teams'
import CustomCalendar from '../../components/core/calendar'
import Tooltip from '../../components/core/tooltip'
import {ArrowLeftIcon, ArrowRightIcon} from '../../icons'
import './index.scss'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const START_HOUR = 9
const DEFAULT_START_HOUR = '00:00'
const DEFAULT_END_HOUR = '23:59'
const POSITION_LEFT = 262
const POSITION_LEFT_MOBILE = 100
const BUTTONS_WIDTH = 250
const MIN_RANGE_WIDTH = 48
const MAX_COLOR_INDEX = 6
const MAX_MEMBER_INDEX = 7
const MOBILE_WIDTH = 612
const MAX_2_DIGITS_TIME = 10
const HOURS_IN_DAY = 24
const intervalWidth = {
    small: 62,
    medium: 64
}

const {isReadOnlyModeEnabled} = envConfig

const getTimeFromCurrentDate = (date) => date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', hour12: false})
const convertTimeNumber = time => parseInt(time.replace(':', ''))

const CustomScheduleTable = ({allSchedules, isTimeRangeExceeded, setActiveSchedules, activeSchedules, getActionsMenu, handleUpdate, handleDelete, openMenu}) => {
    const tableRef = useRef()
    const text = useNamespaceTranslations('on-call-scheduling')
    const isMembersLoading = useSelector(state => state.members.isMembersLoading)
    const isScheduleLoading = useSelector(state => state.incident_management.isScheduleLoading)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [currentDate, setCurrentDate] = useState(new Date())
    const [selectedDateRange, setSelectedDateRange] = useState(null)
    const [leftMargin, setLeftMargin] = useState(null)

    const formatDateObj = (date) => {
        const dateObj = normalize(date, DateFormats.DOT_MDY)
        const {weekdays, months} = text
        return `${weekdays[dateObj.getDay()]?.value}, ${months.find((_, index) => index === dateObj.getMonth())?.label} ${dateObj.getDate()}`
    }

    const calculateStyles = (startTime, endTime) => {
        const startHourValue = convertTimeNumber(startTime) - convertTimeNumber(DEFAULT_START_HOUR)
        const convertedStartTime = isTimeRangeExceeded(endTime, startTime) ? DEFAULT_START_HOUR : startTime
        const convertedEndTime = isTimeRangeExceeded(endTime, startTime) ? DEFAULT_END_HOUR : endTime
        const startHour = parseInt(getValueFromTime(convertedStartTime, 0))
        const endHour = parseInt(getValueFromTime(convertedEndTime, 0))
        const startMin = parseInt(getValueFromTime(convertedStartTime, 1))
        const endMin = parseInt(getValueFromTime(convertedEndTime, 1))

        let width = (Math.abs(endHour - startHour)) * intervalWidth.medium +  Math.abs(endMin - startMin)
        let left = `${(Math.abs(startHourValue) / 100) * intervalWidth.medium + leftMargin + getValueFromTime(startTime, 1) / 3}px`

        if (isTimeRangeExceeded(endTime, startTime)) {
            left = POSITION_LEFT
        }

        return {left, width}
    }

    const handleDateChange = (amount) => {
        const newDate = new Date(currentDate)
        newDate.setDate(newDate.getDate() + amount)
        setCurrentDate(newDate)
    }

    const getExistingSchedules = (uniqueSchedules) => {
        return uniqueSchedules?.filter((schedule) =>
            isBeforeOrEqual(schedule?.start_date, currentDate, DateFormats.ISO_DATETIME) &&
        isAfterOrEqual(schedule?.end_date, currentDate, DateFormats.ISO_DATETIME) &&
        schedule.months_of_year?.includes(currentDate.getMonth() + 1) && schedule.week_days?.includes(currentDate.getDay())
        )?.map(schedule => ({
            ...schedule,
            start_date: formatDate(schedule.start_date, DateFormats.ISO_DATETIME),
            end_date: formatDate(schedule.end_date, DateFormats.ISO_DATETIME),
            start_time: formatTimeString(utcToLocal(schedule.start_time)),
            end_time: formatTimeString(utcToLocal(schedule.end_time))
        }))
    }

    const mergeTimeIntervals = currentSchedules => {
        if (!currentSchedules || currentSchedules.length === 0) {
            return []
        }

        let adjustedSchedules = currentSchedules.map(schedule => ({...schedule, start_time: isTimeRangeExceeded(schedule.end_time, schedule.start_time) ? DEFAULT_START_HOUR : schedule.start_time, end_time: isTimeRangeExceeded(schedule.end_time, schedule.start_time) ? DEFAULT_END_HOUR : schedule.end_time}))

        adjustedSchedules.sort((a, b) => {
            return  a.start_time !== b.start_time
                ? compareTime(a.start_time, b.start_time)
                : compareTime(a.end_time, b.end_time)
        })

        const mergedSchedules = [adjustedSchedules[0]]

        for (let i = 1; i < adjustedSchedules.length; i++) {
            let currentInterval = mergedSchedules[mergedSchedules.length - 1]
            const nextInterval = adjustedSchedules[i]

            if (currentInterval.end_time >= nextInterval.start_time && currentInterval.start_time <= nextInterval.end_time) {
                currentInterval.end_time = isTimeAfter(currentInterval.end_time, nextInterval.end_time) > 0 ?
                    currentInterval.end_time : nextInterval.end_time
                currentInterval.start_time = isTimeBefore(nextInterval.start_time, currentInterval.start_time) < 0 ?
                    nextInterval.start_time : currentInterval.start_time
            } else {
                mergedSchedules.push(nextInterval)
            }
        }

        return mergedSchedules
    }

    const returnActionButtons = (member, schedule) => {
        return <>
            <button
                onClick={() => handleUpdate(member, schedule)}
                disabled={isReadOnlyModeEnabled}
            >
                <span className="button-text">{text.edit}</span>
            </button>
            <button className="outline-red"
                onClick={() => handleDelete(member, schedule)}
                disabled={isReadOnlyModeEnabled}
            >
                <span className="button-text">{text.delete}</span>
            </button>
        </>
    }

    const TimeRange = ({styles, isLarge, colorsIndex, hasSmallButton, member, schedule, memberIndex}) => (
        <td style={styles} key={memberIndex} className={classNames(`range range-custom-${colorsIndex}`, {
            dark: isDarkMode
        })}>
            {isLarge && <span className="from-to">{isTimeRangeExceeded(schedule?.end_time, schedule?.start_time) ? `${DEFAULT_START_HOUR} - ${DEFAULT_END_HOUR}` : `${schedule?.start_time} - ${schedule?.end_time}`}</span>}
            <div className={classNames({
                'edit-delete': true,
                'active-btn': !hasSmallButton
            })}>
                {returnActionButtons(member, schedule)}
            </div>
            <div className={classNames({
                'edit-delete-small': true,
                'active-btn': hasSmallButton,
                'with-time': isLarge
            })}>
                {getActionsMenu(member, schedule, true, !isLarge && (isTimeRangeExceeded(schedule.end_time, schedule.start_time) ? `${DEFAULT_START_HOUR} - ${DEFAULT_END_HOUR}` : `${schedule?.start_time} - ${schedule.end_time}`))}
                {!isLarge && <span className="from-to">{isTimeRangeExceeded(schedule.end_time, schedule.start_time) ? `${DEFAULT_START_HOUR} - ${DEFAULT_END_HOUR}` : `${schedule?.start_time} - ${schedule.end_time}`}</span>}
            </div>
        </td>
    )

    const isProcessing = isScheduleLoading || isMembersLoading || isFirstLoad

    useEffect(() => {
        setIsFirstLoad(false)
    }, [])

    useEffect(() => {
        if (selectedDateRange) {
            setCurrentDate(normalize(selectedDateRange, DateFormats.DOT_DMY))
        }
    }, [selectedDateRange])

    useEffect(() => {
        if (currentDate && selectedDateRange) {
            setSelectedDateRange(formatDate(currentDate, DateFormats.DOT_DMY))
        }
    }, [currentDate])

    useEffect(() => {
        const setInitialScroll = () => {
            if (tableRef.current) {
                tableRef.current.scrollLeft = intervalWidth.small * START_HOUR
            }
            setLeftMargin(window.innerWidth <= MOBILE_WIDTH ? POSITION_LEFT_MOBILE : POSITION_LEFT)
        }

        setInitialScroll()

        window.addEventListener('resize', setInitialScroll)

        return () => {
            window.removeEventListener('resize', setInitialScroll)
        }
    }, [tableRef?.current, isProcessing])

    useEffect(() => {
        setActiveSchedules({member: activeSchedules?.member, schedules: mergeTimeIntervals(getExistingSchedules(allSchedules?.find(schedule => schedule.member?.customer_id === activeSchedules?.member?.customer_id)?.schedules))})
    }, [allSchedules])

    return (
        <>
            <div className='schedule-actions'>
                {Customer.isRoot() && (
                    <TeamSwitcher isInfoDisabled/>
                )}
                <CustomCalendar
                    placeholder={text.date_range}
                    onChange={(date) => {
                        setSelectedDateRange(formatDate(date, DateFormats.DOT_DMY))
                    }}
                    value={selectedDateRange}
                    currentDate={normalize(selectedDateRange, DateFormats.DOT_DMY)}
                    isInfoDisabled
                />
            </div>
            <div className={classNames('content-body', {
                dark: isDarkMode
            })}>
                <div className="content-header">
                    {isProcessing ? renderSkeletonLoaders(1) :
                        <div className={classNames('date-switcher', {
                            dark: isDarkMode
                        })}>
                            <button onClick={() => handleDateChange(-1)}><ArrowLeftIcon color={colors.colorPrimary}/></button>
                            <button onClick={() => handleDateChange(1)}><ArrowRightIcon color={colors.colorPrimary}/></button>
                            <span>{formatDateObj(currentDate)}</span>
                        </div>}
                </div>
                {isProcessing ? <span className='loader-container'>{renderSkeletonLoaders(4)}</span> :
                    <div className={classNames('chart', {
                        dark: isDarkMode
                    })} ref={tableRef}>
                        <table className="chart-container">
                            <thead className="chart-header">
                                <tr className="by-hours active">
                                    <th className="chart-title">{text.members}</th>
                                    {Array.from({length: 24}, (_, index) => {
                                        const hour = index
                                        const formattedHour = hour < HOURS_IN_DAY ? (hour < MAX_2_DIGITS_TIME ? `0${hour}:00` : `${hour}:00`) : `0${hour % HOURS_IN_DAY}:00`
                                        return (
                                            <th key={index} className="time-track">
                                                {formattedHour}
                                            </th>
                                        )
                                    })
                                    }
                                </tr>
                            </thead>
                            <tbody className="chart-body">
                                {allSchedules?.length > 0 ? allSchedules.map(({member, schedules}, memberIndex) => {
                                    const colorsIndex = Math.min(memberIndex >= MAX_MEMBER_INDEX ? allSchedules.length % memberIndex : memberIndex, MAX_COLOR_INDEX)
                                    const uniqueSchedules = schedules ? mergeTimeIntervals(getExistingSchedules(schedules)) : []
                                    const currentTime = getTimeFromCurrentDate(currentDate)

                                    const isOnCall = uniqueSchedules.length > 0
                                        ? uniqueSchedules.find(schedule =>
                                            isTimeAfterOrEqual(currentTime, schedule.start_time) &&
                                            isTimeBeforeOrEqual(currentTime, schedule.end_time)
                                        )
                                        : true
                                    return (
                                        <tr className="hours" key={memberIndex}>
                                            <td className={classNames('member', {
                                                higher: openMenu[memberIndex + 1]
                                            })}>
                                                <span className="member-details">
                                                    <span className="member-info">
                                                        <span className='member-profile'>
                                                            <Tooltip placement="top" trigger="mouseenter" content={isOnCall ? text.on_call : text.not_on_call} containerClassname={isOnCall ? 'on-call' : 'not-on-call'}>
                                                                <span className={classNames('circle', {
                                                                    'on-call': isOnCall
                                                                })}/>
                                                            </Tooltip>
                                                            <span className={classNames(`avatar bg-color-${colorsIndex}`, {
                                                                dark: isDarkMode
                                                            })}>
                                                                {Customer.getUserFullNameFirstChars(member)}</span>
                                                        </span>
                                                        <h4 className="member-name">{member.name} {member.surname}</h4>
                                                    </span>
                                                    <span className="menu-container">{getActionsMenu(member, uniqueSchedules, false, false, memberIndex + 1)}</span>
                                                </span>
                                            </td>
                                            {Array.from({length: 24}, (_, index) => <td  className="hour" key={index}></td>)}
                                            {uniqueSchedules?.map((schedule, memberIndex) => {
                                                const styles = calculateStyles(schedule.start_time, schedule.end_time)
                                                const isLarge = styles.width >= MIN_RANGE_WIDTH
                                                const hasSmallButton = styles.width < BUTTONS_WIDTH

                                                return (
                                                    <TimeRange
                                                        memberIndex={memberIndex}
                                                        styles={{left: styles.left, width: `${styles.width}px`, paddingLeft: !isLarge ? '2px' : hasSmallButton ? '10px' : '', paddingRight: isLarge && '6px'}}
                                                        hasSmallButton={hasSmallButton}
                                                        isLarge={isLarge}
                                                        member={member}
                                                        schedule={schedule}
                                                        colorsIndex={colorsIndex}
                                                    />
                                                )
                                            }
                                            )}
                                        </tr>)
                                }
                                ) : <tr><td className="no-content">{text.no_members}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                }
            </div>
        </>
    )
}

export default CustomScheduleTable
